import { useState, type ReactNode } from 'react';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { Snackbar } from '@/shared/components/Snackbar';
import { Tooltip } from '@/shared/components/Tooltip';
import FilePDFIcon from '@/assets/svg/FilePDF.svg';
import FilePNGIcon from '@/assets/svg/FilePNG.svg';
import FileJPGIcon from '@/assets/svg/FileJPG.svg';
import FileJPEGIcon from '@/assets/svg/FileJPEG.svg';
import type { ViewClient, ViewClientDoc } from '../../api/viewClientApiService';
import { CLIENT_LABELS } from '../../constants/labels';
import { SHARED_MESSAGES } from '@/shared/constants/messages';

/** Formats a byte count into a human-readable size string (B / KB / MB). */
function formatFileSize(bytes: number | null | undefined): string {
  if (!bytes || bytes <= 0) {
    return '—';
  }
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  if (bytes >= 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${bytes} B`;
}

/** Opens a document URL in a new browser tab with noopener/noreferrer. */
function openDocInNewTab(url: string): void {
  window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * Downloads a document via fetch and triggers a browser save dialog.
 * Falls back to opening in a new tab if the fetch fails (e.g. CORS restriction).
 */
async function downloadDocToLocal(
  url: string,
  filename: string
): Promise<void> {
  try {
    const response = await fetch(url, { mode: 'cors' });
    if (!response.ok) {
      return;
    }
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  } catch {
    openDocInNewTab(url);
  }
}

/** Returns the appropriate file-type icon SVG component based on the file extension. */
function getDocIcon(filename: string): ReactNode {
  const ext = filename.split('.').pop()?.toLowerCase() ?? '';
  switch (ext) {
    case 'pdf':
      return <FilePDFIcon />;
    case 'png':
      return <FilePNGIcon />;
    case 'jpeg':
      return <FileJPEGIcon />;
    case 'jpg':
      return <FileJPGIcon />;
    default:
      return <FilePDFIcon />;
  }
}

/** Props for the ViewClientComplianceDetails component. */
interface ViewClientComplianceDetailsProps {
  client: ViewClient | null;
}

/**
 * Displays the "Dane zgodności" tab of the View Client page.
 *
 * Renders a single card containing:
 * - **NIP** — Polish tax identification number
 * - **Government-ID docs** — downloadable compliance documents with file-type
 *   icons, filename tooltips, file size labels, and a download button that
 *   triggers a local save (with new-tab fallback on CORS failure)
 *
 * Shows an empty-state message when no documents are attached.
 */
const ViewClientComplianceDetails = ({
  client,
}: ViewClientComplianceDetailsProps) => {
  const [showDownloadSnackbar, setShowDownloadSnackbar] = useState(false);
  const docs: ViewClientDoc[] = client?.complianceDetails?.docs ?? [];
  const hasDocs = docs.length > 0;
  const nip = client?.complianceDetails?.nip?.trim() || '—';

  const handleDownload = (url: string, filename: string): void => {
    setShowDownloadSnackbar(true);
    downloadDocToLocal(url, filename);
  };

  return (
    <div className="flex flex-col gap-6 m-[6px]">
      <div className="flex flex-col gap-4 border border-[#DCDDE0] rounded-[8px] p-4">
        <p
          className="text-titleDefault font-medium text-on-surface-variant"
          data-testid="view-client-compliance-title"
        >
          {CLIENT_LABELS.VIEW_CLIENT_COMPLIANCE_TITLE}
        </p>

        {/* TIN / NIP */}
        <div className="flex flex-col gap-2">
          <h2 className="text-bodySmall font-normal text-on-surface-variant">
            {CLIENT_LABELS.VIEW_CLIENT_COMPLIANCE_NIP_LABEL}
          </h2>
          <p
            className="text-bodyLarge font-normal text-neutral-10"
            data-testid="view-client-nip"
          >
            {nip}
          </p>
        </div>

        {/* Government ID docs */}
        {hasDocs && (
          <h3
            className="text-bodySmall font-normal text-on-surface-variant"
            data-testid="view-client-government-id-heading"
          >
            {CLIENT_LABELS.VIEW_CLIENT_COMPLIANCE_GOV_ID_LABEL}
          </h3>
        )}

        {hasDocs ? (
          <ul className="flex flex-col gap-2 list-none p-0 m-0">
            {docs.map((doc, index) => {
              const url = doc.url?.trim();
              const filename = doc.filename ?? '—';
              const fileSize = doc.fileSize ?? 0;
              const sizeLabel = formatFileSize(fileSize);

              return (
                <div
                  key={doc.resourceId ?? index}
                  className="flex flex-col gap-4"
                >
                  <li className="flex items-center justify-between p-2 bg-white rounded">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="shrink-0">
                        <Tooltip
                          content="View file"
                          side="bottom"
                          align="center"
                          contentMaxWidth={80}
                          useContentWidth
                          forceShow
                          triggerTabIndex={-1}
                        >
                          <button
                            type="button"
                            onClick={() => url && openDocInNewTab(url)}
                            className="shrink-0 p-0 border-0 bg-transparent cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-color-30 rounded"
                            style={{ width: '40px', height: '40px' }}
                            aria-label={`Otwórz ${filename}`}
                            data-testid={`view-client-doc-icon-${index}`}
                          >
                            {getDocIcon(filename)}
                          </button>
                        </Tooltip>
                      </div>
                      <div className="flex flex-col min-w-0 flex-1">
                        <Tooltip
                          content={filename}
                          side="bottom"
                          align="center"
                          contentMaxWidth={500}
                          useContentWidth
                        >
                          <p className="text-[16px] leading-6 tracking-[0.5px] text-[#2A4455] font-normal truncate">
                            {filename}
                          </p>
                        </Tooltip>
                        <p className="text-[12px] leading-4 tracking-[0.4px] text-[#878B8E] font-normal">
                          {sizeLabel}
                        </p>
                      </div>
                    </div>
                    <div className="shrink-0">
                      <Tooltip
                        content="Download file"
                        side="bottom"
                        align="center"
                        contentMaxWidth={120}
                        useContentWidth
                        forceShow
                        triggerTabIndex={-1}
                      >
                        <button
                          type="button"
                          onClick={() => url && handleDownload(url, filename)}
                          className="shrink-0 p-1 rounded border-0 bg-transparent cursor-pointer text-[#474A4C] hover:bg-[#474A4C14] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-color-30 disabled:opacity-50 disabled:cursor-not-allowed"
                          aria-label={`Pobierz ${filename}`}
                          data-testid={`view-client-doc-download-${index}`}
                          disabled={!url}
                        >
                          <ArrowDownTrayIcon className="w-6 h-6" />
                        </button>
                      </Tooltip>
                    </div>
                  </li>
                </div>
              );
            })}
          </ul>
        ) : (
          <div className="flex flex-col items-center justify-center py-4">
            <p
              className="text-bodyMedium text-on-surface-variant"
              data-testid="view-client-compliance-empty"
            >
              Brak danych do wyświetlenia.
            </p>
          </div>
        )}
      </div>

      <Snackbar
        open={showDownloadSnackbar}
        message={SHARED_MESSAGES.DOWNLOAD_READY_MESSAGE}
        variant="success"
        onClose={() => setShowDownloadSnackbar(false)}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton
      />
    </div>
  );
};

export default ViewClientComplianceDetails;
